/**
 * The Activity page — every live run across every workflow, the page the
 * list's strip has been promising. Two sections: what needs a person
 * (waiting, failed) on top, everything else under it, both newest first.
 * Rows expand into the same step timeline the per-workflow history uses.
 *
 * `?status=` filters (URL first, per the house rule — a filtered view is a
 * shareable link; the strip's failure link lands on ?status=failed).
 */
import { Fragment, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  Skeleton,
  Table,
  type BadgeVariant,
} from '@servicecompressionllc/sc-design-system';
import { useQuery } from '@tanstack/react-query';
import { AppShell } from '../../components/AppShell.js';
import { RunStepsTimeline } from '../events/EventRuns.js';
import { useMe } from '../../hooks/useMe.js';
import * as api from './api.js';

/** Engine words never reach the page — same translation the runs page uses. */
const RUN_STATUS: Record<string, { label: string; variant: BadgeVariant }> = {
  done: { label: 'Finished', variant: 'success' },
  suspended: { label: 'Waiting on someone', variant: 'warning' },
  deferred: { label: 'Paused — daily limit', variant: 'info' },
  failed: { label: 'Something went wrong', variant: 'danger' },
  running: { label: 'Running', variant: 'info' },
};
const runStatus = (s: string): { label: string; variant: BadgeVariant } =>
  RUN_STATUS[s] ?? { label: s, variant: 'info' };

const FILTERS = [
  { value: '', label: 'Everything' },
  { value: 'failed', label: 'Couldn’t finish' },
  { value: 'suspended', label: 'Waiting on someone' },
] as const;

const NEEDS_A_PERSON = new Set(['failed', 'suspended']);

export const ActivityPage = () => {
  const { data: me } = useMe();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const [openRunId, setOpenRunId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['workflow-activity', me?.tenant.id, status],
    queryFn: () => api.fetchActivity(status || undefined),
    enabled: Boolean(me?.tenant.id),
  });
  const [showDeleted, setShowDeleted] = useState(false);
  const allRuns = query.data ?? [];
  // Deleted-workflow rows are history, not activity: identically labeled and
  // linking nowhere, they dominated the list. Tucked behind a disclosure.
  const deletedCount = allRuns.filter((r) => r.workflowDeleted).length;
  const runs = showDeleted ? allRuns : allRuns.filter((r) => !r.workflowDeleted);
  // Needs-attention floats above the rest; each group stays newest-first.
  const sorted = [...runs].sort(
    (a, b) => Number(NEEDS_A_PERSON.has(b.status)) - Number(NEEDS_A_PERSON.has(a.status)),
  );

  return (
    <AppShell>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Activity</h1>
          <p className="text-sm text-ink-muted">
            Every run, across all your workflows —{' '}
            <Link to="/settings/workflows" className="text-primary hover:underline">
              back to Workflows
            </Link>
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              aria-pressed={status === f.value}
              onClick={() => {
                // Default stays OUT of the URL, per the house rule.
                if (f.value === '') params.delete('status');
                else params.set('status', f.value);
                setParams(params, { replace: true });
              }}
              className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                status === f.value
                  ? 'border-primary bg-primary text-white'
                  : 'border-outline-strong text-ink-muted hover:text-ink'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {query.isLoading && <Skeleton className="h-48 w-full" />}
      {query.isError && <Banner variant="danger">Couldn’t load activity.</Banner>}
      {deletedCount > 0 && (
        <p className="mb-3 text-xs text-ink-muted">
          {showDeleted
            ? `Showing ${String(deletedCount)} run${deletedCount === 1 ? '' : 's'} from deleted workflows. `
            : `${String(deletedCount)} run${deletedCount === 1 ? '' : 's'} from deleted workflows ${deletedCount === 1 ? 'is' : 'are'} hidden. `}
          <button
            type="button"
            className="text-primary hover:underline"
            onClick={() => setShowDeleted((v) => !v)}
          >
            {showDeleted ? 'Hide them' : 'Show them'}
          </button>
        </p>
      )}
      {!query.isLoading && runs.length === 0 && (
        <Banner variant="info">
          Nothing here{status ? ' for this filter' : ' yet'} — runs appear as soon as an enabled
          workflow reacts to an event.
        </Banner>
      )}

      {sorted.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-outline">
          <Table.Root className="w-full min-w-[48rem] text-sm">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Workflow</Table.HeaderCell>
                <Table.HeaderCell>Started</Table.HeaderCell>
                <Table.HeaderCell>Event</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {sorted.map((run) => (
                <Fragment key={run.id}>
                  <Table.Row
                    className="cursor-pointer"
                    onClick={() => setOpenRunId(openRunId === run.id ? null : run.id)}
                  >
                    <Table.Cell>
                      {run.workflowDeleted ? (
                        // Its builder page 404s — a link would be a trap. The
                        // pinned definition carries steps, not the name, so
                        // the historical name is genuinely gone.
                        <span className="font-medium text-ink-muted">
                          {run.workflowName}
                          <span className="ml-1.5 text-xs text-ink-subtle">(deleted)</span>
                        </span>
                      ) : (
                        <Link
                          to={`/settings/workflows/${run.workflowId}`}
                          onClick={(e) => e.stopPropagation()}
                          className="font-medium text-primary hover:underline"
                        >
                          {run.workflowName}
                        </Link>
                      )}
                    </Table.Cell>
                    <Table.Cell className="whitespace-nowrap text-ink-muted">
                      {new Date(run.startedAt).toLocaleString()}
                    </Table.Cell>
                    <Table.Cell className="text-xs text-ink-muted">{run.eventType}</Table.Cell>
                    <Table.Cell>
                      <Badge variant={runStatus(run.status).variant}>
                        {runStatus(run.status).label}
                      </Badge>
                    </Table.Cell>
                  </Table.Row>
                  {openRunId === run.id && (
                    <Table.Row>
                      <Table.Cell colSpan={4} className="bg-surface">
                        <div className="py-1">
                          <RunStepsTimeline steps={run.steps} ariaLabel={`Run ${run.id} steps`} />
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Fragment>
              ))}
            </Table.Body>
          </Table.Root>
        </div>
      )}
    </AppShell>
  );
};
